import { Callout, HelpLink, Related, ScreenLink } from "./parts";

export function QuotasAndNotifications() {
  return (
    <>
      <p>
        A quota caps spend, tokens or requests for an organization, a team or a person, per UTC day, week or month. The
        customer's proxy enforces it across all its tasks and installations, and a developer who reaches it is told so
        in Claude Code or Codex. Each customer's quotas are on its <strong>Quotas</strong> tab, and a quota opens under the
        customer's tab bar; fleet-wide standing is
        on <ScreenLink href="/quotas">Quota standing</ScreenLink>. Quotas never change a bill: a refused request is
        recorded with zero tokens and is never priced.
      </p>

      <h2 id="quotas">Quotas</h2>
      <p>Each quota has:</p>
      <ul>
        <li>
          <strong>Applies to</strong>: the whole organization, one team or one person (one total each), or each member
          of the organization or of a team (everyone gets the cap separately).
        </li>
        <li>
          <strong>Measure</strong>: spend in dollars, tokens (input, output and cache writes; cache reads are not
          counted) or requests. Spend counts the organization's own price; AWS list cost is for AllCode's guardrails.
        </li>
        <li>
          <strong>Period</strong>: each UTC day, each week from Monday 00:00 UTC, or each calendar month.
        </li>
        <li>
          <strong>Warn at</strong>: up to five percentages (75 and 95 by default) at which developers and webhooks are
          warned, once per period.
        </li>
        <li>
          <strong>When reached</strong>: <strong>Block requests</strong> refuses once the limit and any grace are used
          up; <strong>Warn only</strong> never refuses.
        </li>
        <li>
          <strong>Grace</strong>: how far past the limit a blocking cap still admits, 0 to 50 % (0 refuses at 100 %).
        </li>
        <li>
          <strong>If the proxy cannot check</strong>: when the proxy cannot reach Nexus for longer than its grace
          window (15 minutes by default), <strong>Admit requests</strong> (the default) keeps serving and{" "}
          <strong>Refuse requests</strong> turns them away.
        </li>
      </ul>
      <p>
        Every cap that applies to a request is checked, and the tightest refuses first. For each measure and period a
        person's own cap replaces their team's per-member cap, which replaces the organization's. A service key is
        held only to organization caps. A cap larger than the one above it is saved with a warning; the larger one
        above still bounds it.
      </p>
      <p>
        Before saving, the <strong>Preview</strong> shows what the cap would have done over the last complete period:
        who would have passed each warning and the limit, and on which day. Lowering a limit below what is already used
        refuses the next request at once. A quota whose scope, measure or period must change is replaced by a new one;{" "}
        <strong>Disable</strong> keeps one with its history, <strong>Delete</strong> retires it.
      </p>
      <h3 id="read-only-quotas">Read-only quotas</h3>
      <p>
        Two badges mark quotas you cannot edit here. <strong>AllCode guardrail</strong>: a cap AllCode owns, usually at
        AWS list cost on an account whose AWS bill AllCode pays; customers see it read-only, and AllCode's super-admins and billing admins manage it.{" "}
        <strong>From installation</strong>: a cap the proxy's deployment declares in its settings. Change or remove it
        in the deployment; once no running proxy task declares it, it stops applying (a task that just stopped can keep
        it for up to 24 hours).
      </p>

      <h2 id="standing">Standing</h2>
      <p>
        <strong>Grants and standing</strong> opens one quota for this period: a row per organization, team or member
        with what is used, the limit with any grants, and the state.
      </p>
      <table>
        <thead>
          <tr>
            <th>State</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>OK</td>
            <td>Below the first warning.</td>
          </tr>
          <tr>
            <td>Warning</td>
            <td>Past a warning percentage.</td>
          </tr>
          <tr>
            <td>In grace</td>
            <td>Past the limit, still served within the grace.</td>
          </tr>
          <tr>
            <td>Exhausted</td>
            <td>Requests are refused (or, for a warn-only cap, the limit is passed).</td>
          </tr>
          <tr>
            <td>Exempt</td>
            <td>A grant lifts the cap.</td>
          </tr>
        </tbody>
      </table>
      <p>
        <strong>Refused now</strong> lists who is being turned away. <strong>History</strong> lists each warning,
        exhaustion and grant once. The chip beside the title says when the counters were last updated by a proxy:{" "}
        <strong>Never synced</strong> until a proxy of the organization has reported, <strong>Stale</strong> after 10
        minutes without one.
      </p>
      <p>
        Dollar figures follow the customer's money rules. AllCode staff (super-admins and billing users) see them all. A
        customer admin sees spend in the organization's own price for billed months, and AWS list cost only when the
        customer's cost visibility allows it; where neither is shown, a spend cap shows only its percentage and state.
        See <HelpLink to="rate-plans" section="cost-visibility" />.
      </p>

      <h2 id="grants">Grants</h2>
      <p>
        A grant raises a cap for a while without editing it. <strong>Increase the limit</strong> adds an amount;{" "}
        <strong>Exempt from the limit</strong> lifts refusal altogether. On a per-member cap a grant covers one person
        or every member. It lasts at most 31 days, needs a reason of 10 to 500 characters, is recorded in the{" "}
        <HelpLink to="audit-log" />, and reaches every proxy within seconds, so a blocked developer's next request is
        served. Revoke ends it at once; the row stays in the list.
      </p>
      <Callout>
        A grant cannot be added to a disabled quota, or to one declared by the deployment: those belong to whoever owns
        the quota.
      </Callout>

      <h2 id="fleet">Quota standing across customers</h2>
      <p>
        AllCode staff see every customer with an active quota on <ScreenLink href="/quotas">Quota standing</ScreenLink>,
        ranked by its fullest quota, with how many people, teams or totals are in warning, in grace and exhausted, how
        many guardrails it carries and who pays its AWS bill. Filter by state or owner, export CSV, and open a customer
        to its Quotas tab. The Home tile counts the customers with a quota exhausted, in grace or past a warning.
      </p>

      <h2 id="what-developers-see">What developers see</h2>
      <p>
        At the limit the developer's request is refused with a message naming the cap, its period and the reset in UTC,
        for example “Your team's monthly usage limit is reached (resets 2026-11-01T00:00:00Z) — request an increase at
        …”. Claude Code prints that message; Codex shows its own “You've hit your usage limit. Try again at …” instead.
        Codex also shows how much is used as the cap fills; Claude Code, as Nexus configures it, shows nothing before
        the limit. The address after “request an increase at” is the organization's increase page, set in its settings;
        without one the message says “ask your Nexus admin for an increase.” See{" "}
        <HelpLink to="claude-code-and-codex" />.
      </p>
      <p>
        While the proxy's quota enforcement is set to <strong>shadow</strong> (the first release's default), it counts
        and decides but refuses nothing and tells developers nothing. Switch it to <strong>on</strong> in the
        installation's settings once the counts look right.
      </p>

      <h2 id="notifications">Notifications</h2>
      <p>
        The customer's <strong>Notifications</strong> tab, which the customer's admins also have on their proxy's Admin,
        holds up to 10 webhooks. Nexus posts to each when a quota
        passes a warning or is exhausted, once per quota, person or team, period and threshold. A webhook is{" "}
        <strong>Generic JSON</strong> (for your own service) or <strong>Slack</strong> (a Slack incoming webhook). The
        URL must be https and public; it is stored encrypted, and only its host is shown again. Spend figures are never
        sent; token and request counts are.
      </p>
      <ul>
        <li>
          The signing secret is shown once, when you create the webhook or <strong>Rotate secret</strong>. Each post
          carries an <code>X-Nexus-Signature</code> header the receiver checks with it.
        </li>
        <li>
          <strong>Send test</strong> posts a test event now and shows the answer.
        </li>
        <li>
          A failed post is retried with growing gaps for 24 hours. A webhook whose posts all failed for 24 hours is{" "}
          <strong>Failing</strong>: fix the receiver or the URL, then send a test; a success clears it. The delivery log
          shows each event's attempts and the last error.
        </li>
      </ul>

      <h2 id="known-limits">Known limits</h2>
      <ul>
        <li>Grants do not send a webhook yet, although the event type can be chosen.</li>
        <li>
          Team caps can miss the usage of people who sign in with a key: their first request may move them into a
          duplicate team. A fix that merges those teams back is in progress.
        </li>
        <li>There are no email notifications; use a webhook.</li>
      </ul>

      <Related to={["users-teams-and-sign-in", "rate-plans", "audit-log", "claude-code-and-codex", "glossary"]} />
    </>
  );
}
